import { Component, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { map } from 'rxjs/operators';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { DataRefFormat, Domain, Language, Market } from '@shared/models/dataref.model';
import { RefDataService } from '@services/ref-data.service';
import { NotificationService } from '@services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { PartnerService } from '@services/partner/partner.service';
import { DetailsPartner, Partner, PartnerType } from '@shared/models/partner.model';
import { TextPopinComponent } from '@shared/components/text-popin/text-popin.component';
import { SelectMarketDialogComponent } from './select-market-dialog/select-market-dialog.component';
import { RecipeSearchResult, SearchPayload } from '@shared/models/search.model';
import { Sort } from '@angular/material/sort';
import { SearchService } from '@services/search.service';
import { PAGE_SIZE } from '@shared/constants/page.constant';
import { ListRecipeComponent } from './list-recipe/list-recipe.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-partners-edit',
  templateUrl: './partners-edit.component.html',
  styleUrls: ['./partners-edit.component.scss'],
  standalone: false,
})
export class PartnersEditComponent implements OnInit {
  @ViewChild(ListRecipeComponent) resultComponent?: ListRecipeComponent;

  form = this.formBuilder.group({});
  partner: Partner | null = null;
  domainsList: Domain[] = [];
  marketsList: Market[] = [];
  languagesList: Language[] = [];
  partnerTypes: PartnerType[] = [];
  statusesRefData: DataRefFormat[] = [];
  searchResult: Observable<RecipeSearchResult | undefined> = of(void 0);
  domains: Observable<Domain[]> = of([]);
  pageIndex = 0;
  pageSize = PAGE_SIZE;
  currentLangue = '';
  currentMarket = '';
  currentTabIndex = 0;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private refDataService: RefDataService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private partnerService: PartnerService,
    private searchService: SearchService,
    @Inject(LOCALE_ID) public locale: string,
  ) {}

  ngOnInit(): void {
    this.refDataService.getByKey('partnerTypes', true).subscribe((partnerTypes) => {
      this.partnerTypes = (Object.values(partnerTypes) as PartnerType[]).filter((partner: PartnerType) => partner?.displayType);
    });

    this.refDataService.getByKey('markets', true).subscribe((allMarkets) => {
      this.marketsList = this.refDataService.translateReference(this.refDataService.toArray(allMarkets));
    });

    this.refDataService.getByKey('languages', true).subscribe((allLanguages) => {
      this.languagesList = this.refDataService.translateReference(this.refDataService.toArray(allLanguages));
    });

    this.domains = this.refDataService
      .getByKey('domains', true)
      .pipe(map((domains) => this.refDataService.translateReference(this.refDataService.toArray(domains))));

    this.refDataService.getByKey('statuses', true).subscribe((allStatuses) => {
      this.statusesRefData = allStatuses ?? [];
    });

    this.route.data.subscribe(
      ({ partner }: Data) => this.buildForm(partner),
      (httpError: HttpErrorResponse) => {
        if (httpError.status === 404) {
          this.router.navigate(['404']);
        }
      },
    );
  }

  /**
   * Save content data from form
   */
  save(): void {
    if (
      this.form.invalid ||
      this.partner === null ||
      this.hasNameError() ||
      this.hasDescriptionError() ||
      this.hasMediasError() ||
      this.hasBackgroundError()
    ) {
      return;
    }

    const partnerToUpdate: Partner = {
      ...this.partner,
      ...this.form.value,
    };

    this.partnerService.updatePartner(partnerToUpdate, this.partner.key).subscribe(
      () => {
        this.notificationService.sendSuccess('REFDATA.PARTNERS.MESSAGES.SUCCESS');
      },
      (err) => this.errorManager(err),
    );
  }

  errorManager(err: HttpErrorResponse): void {
    if (err.status && err.status === 403) {
      this.notificationService.sendError('REFDATA.PARTNERS.MESSAGES.ERROR');
    } else {
      this.notificationService.sendAPIError(err);
    }
  }

  /**
   * Build form adding control
   */
  buildForm(partner: Partner | null): void {
    this.partner = partner;
    this.form = this.formBuilder.group({
      type: [this.partner?.type ?? ''],
      details: [this.partner?.details ?? []],
      targetDomains: [this.partner?.targetDomains ?? []],
      medias: [this.partner?.medias ?? []],
      backgroundMedias: [this.partner?.backgroundMedias ?? []],
      crossSellingMedias: [this.partner?.crossSellingMedias ?? []],
      signatureMedias: [this.partner?.signatureMedias ?? []],
    });
    if (partner?.details?.length) {
      this.currentLangue = partner?.details[this.currentTabIndex].lang;
      this.currentMarket = partner?.details[this.currentTabIndex].market;
      this.search();
    }
  }

  addMarket(): void {
    const dialogRef = this.dialog.open(SelectMarketDialogComponent, {
      width: window.innerWidth + 'px',
    });
    dialogRef.afterClosed().subscribe((market: Market) => {
      if (market) {
        Object.keys(market.langs).forEach((lang: string) => {
          this.form.controls.details.value.push({ market: market.key, lang });
        });
      }
    });
  }

  deleteMarket(marketKey: string): void {
    const data: any = {
      title: this.translateService.instant('REFDATA.PARTNERS.EDIT.MODAL_DELETE_MARKET'),
      text: this.translateService.instant('REFDATA.PARTNERS.EDIT.MODAL_DELETE_MARKET_CONTENT'),
      cancelBtn: this.translateService.instant('NO'),
      validateBtn: this.translateService.instant('YES'),
    };
    const dialogRef = this.dialog.open(TextPopinComponent, {
      width: window.innerWidth + 'px',
      data,
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.form.controls.details.setValue(
          this.form.controls.details.value.filter((detail: DetailsPartner) => detail.market !== marketKey),
        );
        if (this.form.controls.details.value[this.currentTabIndex]) {
          this.currentLangue = this.form.controls.details.value[this.currentTabIndex].lang;
          this.currentMarket = this.form.controls.details.value[this.currentTabIndex].market;
          this.search();
        }
      }
    });
  }

  getLabelMarket(key: string): string {
    return (this.marketsList?.find((market) => market.key === key) as any)?.label;
  }

  checkDescriptionError(description: string): boolean {
    if (this.form.controls.type.value === 'VISIBLE') {
      return !description;
    }
    return false;
  }

  hasNameError(): boolean {
    return this.form.controls.details.value.some((detail: DetailsPartner) => !detail.name);
  }
  hasDescriptionError(): boolean {
    if (this.form.controls.type.value === 'VISIBLE') {
      return this.form.controls.details.value.some((detail: DetailsPartner) => !detail.description);
    }
    return false;
  }
  hasBackgroundError(): boolean {
    return this.form.controls.backgroundMedias.value.length === 0 && this.form.controls.type.value === 'VISIBLE';
  }
  hasMediasError(): boolean {
    return this.form.controls.medias.value.length === 0 && this.form.controls.type.value === 'VISIBLE';
  }

  changeTab(e: number): void {
    this.currentTabIndex = e;
    this.currentLangue = this.form.controls.details.value[e].lang;
    this.currentMarket = this.form.controls.details.value[e].market;
    this.search();
  }

  search(): void {
    this.searchResult = this.searchService
      .searchRecipe(this.buildSearchPayload(this.resultComponent ? this.resultComponent.sort : undefined), {
        q: '',
        size: this.pageSize,
        page: this.pageIndex,
        market: this.currentMarket,
        lang: this.currentLangue,
        groupBy: 'topRecipe',
      })
      .pipe(
        map((response) => {
          if (!response.content) {
            response.content = [];
          }
          return response;
        }),
      );
  }

  handleSearchSorted(): void {
    this.search();
  }

  handleSearchPage(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.search();
  }

  private buildSearchPayload(sort?: Sort): SearchPayload {
    return {
      fieldList: [
        'title',
        'id',
        'resourceMedias',
        'durations.prepTime',
        'durations.cookingTime',
        'domain',
        'creationDate',
        'modificationDate',
        'lang',
        'status',
        'market',
        'creator',
        'modifier',
        'packs',
        'cookeoBinary',
        'topRecipe.id',
        'brand',
      ],
      facetList: [],
      fieldFilters: [
        { field: 'market.key', values: [this.currentMarket] },
        { field: 'lang.key', values: [this.currentLangue] },
        { field: 'origin.partner.key', values: ['' + this.partner?.key] },
        { field: 'classifications.key_FOOD_COOKING', values: ['IS_FOOD_COOKING'], type: 'exclusion' },
        { field: 'topRecipe.type.key', values: ['TEMPLATE'], type: 'exclusion' },
        { field: 'isAutomaticallyGenerated', values: [true], type: 'exclusion' },
      ],
      sort: {
        name: sort?.active ?? 'title',
        direction: sort?.direction ?? 'ASC',
      },
    };
  }
}
